import React from "react";
import { useRouter } from "next/router";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import { IconV2 } from "components/V2/atoms/IconV2/IconV2";
import { pageName, section, eventName, actionType } from "../../../constants/events/eventName";
import { ChevronLeftArrow, GreenCheckCircle, InactiveCheckCircle } from "../../../constants/icons";
import {
  DEBOUNCE_DELAY,
  SIGN_UP_LANDING_PAGE,
  SUCCESS_AUTH,
  MINIMUM_PASSWORD_LENGTH,
  MAXMIUM_PASSWORD_LENGTH
} from "../../../constants/variables";
import { useAuth } from "../../../context/AuthContext";
import { extractResource } from "../../../helper/extractResource";
import { trackUserActivity } from "../../../services/trackUserActivity";
import { Button } from "../../UI/Button";

function ForgotPasswordResetPage(props) {
  const [values, setValues] = React.useState({
    password: "",
    showPassword: false,
    showConfirmPassword: false
  });
  const [timerId, setTimerId] = React.useState<any>(null);
  const [hasTwelveChars, setHasTwelveChars] = React.useState(false);
  const [hasANumber, setHasANumber] = React.useState(false);
  const [hasUpperCase, setHasUpperCase] = React.useState(false);
  const [hasLowerCase, setHasLowerCase] = React.useState(false);
  const [hasSpecialChar, setHasSpecialChar] = React.useState(false);
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [unMatchedPassword, setUnmatchedPassword] = React.useState(false);
  const [matchedPassword, setMatchedPassword] = React.useState(false);
  const { resourceData } = props;
  const router = useRouter();
  const { signup } = useAuth();

  // ? the function toggles between hide and show password
  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword
    });
  };

  // ? the function toggles between hide and show confirm password
  const handleClickShowConfirmPassword = () => {
    setValues({
      ...values,
      showConfirmPassword: !values.showConfirmPassword
    });
  };

  // ? on mouseDown it shifts the focus away from input fields - From MUI
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  /**
   * the function validates both passwords
   * sets errors if invalid
   * @param e
   */
  const main = (e: any) => {
    if (e.target.name === "password") {
      if (e.target.value.length >= MINIMUM_PASSWORD_LENGTH && e.target.value.length <= MAXMIUM_PASSWORD_LENGTH) {
        setHasTwelveChars(true);
      } else setHasTwelveChars(false);

      if (e.target.value.search(/[A-Z]/g) == -1) {
        setHasUpperCase(false);
      } else setHasUpperCase(true);

      if (e.target.value.search(/[0-9]/) == -1) {
        setHasANumber(false);
      } else setHasANumber(true);

      if (e.target.value.search(/[a-z]/) === -1) {
        setHasLowerCase(false);
      } else setHasLowerCase(true);

      if (e.target.value.search(/[!@#$%^&*(),.?":{}|<>]/) === -1) {
        setHasSpecialChar(false);
      } else setHasSpecialChar(true);
    }

    if (e.target.name === "confirmPassword")
      if (e.target.value === props.formData.password) {
        setMatchedPassword(true);
        setUnmatchedPassword(false);
      } else {
        setUnmatchedPassword(true);
        setMatchedPassword(false);
      }
  };

  /**
   * the function updates the respective state with current input value
   * it also triggers the debounce function
   * @param e
   */
  const handleChange = (e: any) => {
    if (e.target.name === "confirmPassword") setConfirmPassword(e.target.value);
    else {
      props.setFormData({
        ...props.formData,
        [e.target.name]: e.target.value
      });
    }

    debounce(e, DEBOUNCE_DELAY);
  };

  // ? the debounce function calls the main fuction after the specified dealy after user stops typing
  const debounce = (e: any, delay: any) => {
    if (e.target.name === "confirmPassword") setUnmatchedPassword(false); // do not show erros while use is typing

    if (timerId) {
      clearTimeout(timerId);
      setTimerId(null);
    }

    setTimerId(
      setTimeout(() => {
        main(e);
      }, delay)
    );
  };

  /**  the function validates passwords before submission
   *   it sets errors if invalid
   *   if valid, it allows the action call and resets the form data
   */
  const handleSubmit = async () => {
    setIsLoading(true); // begin the loading animation

    if (
      props.formData.password === confirmPassword &&
      hasANumber &&
      hasTwelveChars &&
      hasUpperCase &&
      hasSpecialChar &&
      hasLowerCase
    ) {
      await signup(props.formData).then((response) => {
        if (response == true) {
          // handle the signup event :

          trackUserActivity({
            page: pageName.HOME_PAGE,
            section: section.FLIGHT_BOOKING_PANEL,
            actionType: actionType.PAGE_VIEW,
            eventName: eventName.SIGNUP_SUCCESS
          });
          props.handleState(SUCCESS_AUTH); // on signup flow, success, flow will come here
        }
      });
      // const resp = true; //dummy

      // if (resp == true) {
      //   props.handleState(SUCCESS_AUTH);
      // }
      // else {
      //   if (resp.message != "") {
      //     props.setSignUpError(resp.message);
      //   }
      // }
    } else setIsLoading(false); // if submit data is invalid stop the loading animation
  };

  // ? returns reset password page
  return (
    <div className=" w-full sm:w-80 m-auto h-full flex flex-col justify-between">
      <div className="w-full">
        <div className="flex -mx-1.5">
          <button
            onClick={() => {
              props.handleState(SIGN_UP_LANDING_PAGE);
            }}
          >
            <ChevronLeftArrow />
          </button>
          <p className="font-semibold text-xl mx-3">{extractResource(resourceData, "completeYourSignUp")}</p>
        </div>

        <div className="my-4">
          <div className=" font-medium text-base">{extractResource(resourceData, "letsSecureYourAccount")}</div>
          <div className="flex mt-1">
            <div className="my-0.5 mx-1">
              {hasTwelveChars ? (
                <div className=" text-typography-success">
                  <GreenCheckCircle fill="inherit" />
                </div>
              ) : (
                <InactiveCheckCircle />
              )}
            </div>

            <p className="text-typography-secondary"> {extractResource(resourceData, "eightChars")}</p>
          </div>

          <div className="flex mt-1">
            <div className="my-0.5 mx-1">
              {hasUpperCase ? (
                <div className=" text-typography-success">
                  <GreenCheckCircle fill="inherit" />
                </div>
              ) : (
                <InactiveCheckCircle />
              )}
            </div>
            <p className="text-typography-secondary">{extractResource(resourceData, "atLeast1UpperCase")}</p>
          </div>

          <div className="flex mt-1">
            <div className="my-0.5 mx-1">
              {hasLowerCase ? (
                <div className=" text-typography-success">
                  <GreenCheckCircle fill="inherit" />
                </div>
              ) : (
                <InactiveCheckCircle />
              )}
            </div>
            <p className="text-typography-secondary">{extractResource(resourceData, "atLeast1LowerCase")}</p>
          </div>

          <div className="flex mt-1">
            <div className="my-0.5 mx-1">
              {hasSpecialChar ? (
                <div className=" text-typography-success">
                  <GreenCheckCircle fill="inherit" />
                </div>
              ) : (
                <InactiveCheckCircle />
              )}
            </div>
            <p className="text-typography-secondary">{extractResource(resourceData, "atLeast1SpecialChar")}</p>
          </div>

          <div className="flex mt-1">
            <div className="my-0.5 mx-1">
              {hasANumber ? (
                <div className=" text-typography-success">
                  <GreenCheckCircle fill="inherit" />
                </div>
              ) : (
                <InactiveCheckCircle />
              )}
            </div>
            <p className="text-typography-secondary">{extractResource(resourceData, "atLeast1Num")}</p>
          </div>
        </div>

        <FormControl sx={{ my: 0, width: "100%", height: "48px" }} variant="outlined">
          <InputLabel htmlFor="outlined-adornment-password">
            {extractResource(resourceData, "enterAPassword")}
          </InputLabel>
          <OutlinedInput
            id="outlined-adornment-password"
            type={values.showPassword ? "text" : "password"}
            value={props.formData.password}
            onChange={handleChange}
            name="password"
            sx={{
              "& .css-1p2zgp5-MuiInputBase-root-MuiOutlinedInput-root": {
                borderRadius: "8px 0px 8px 8px"
              },
              height: "48px"
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  sx={{ color: "var(--color-primary)" }}
                >
                  {values.showPassword ? (
                    <IconV2
                      blok={{
                        component: "icon",
                        icon: "visibility-outlined",
                        height: 28,
                        width: 26,
                        alt: "visibility Outlined"
                      }}
                    />
                  ) : (
                    <IconV2
                      blok={{
                        component: "icon",
                        icon: "visibility-off-outlined",
                        height: 28,
                        width: 26,
                        alt: "visibility Off Outlined"
                      }}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            }
            label={extractResource(resourceData, "enterAPassword")}
          />
        </FormControl>
        <FormControl sx={{ mt: 2, width: "100%" }} variant="outlined">
          <InputLabel htmlFor="outlined-adornment">{extractResource(resourceData, "confirmPassword")}</InputLabel>
          <OutlinedInput
            id="outlined-adornment-password"
            type={values.showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={handleChange}
            name="confirmPassword"
            sx={{
              "& .css-1p2zgp5-MuiInputBase-root-MuiOutlinedInput-root": {
                borderRadius: "8px 0px 8px 8px"
              },
              height: "48px"
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowConfirmPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  sx={{ color: "var(--color-primary)" }}
                >
                  {values.showConfirmPassword ? (
                    <IconV2
                      blok={{
                        component: "icon",
                        icon: "visibility-outlined",
                        height: 28,
                        width: 26,
                        alt: "visibility Outlined"
                      }}
                    />
                  ) : (
                    <IconV2
                      blok={{
                        component: "icon",
                        icon: "visibility-off-outlined",
                        height: 28,
                        width: 26,
                        alt: "visibility Off Outlined"
                      }}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            }
            label={extractResource(resourceData, "confirmPassword")}
          />
        </FormControl>

        <p className={`text-typography-error text-sm m-1 ${unMatchedPassword ? "opacity-100" : "opacity-0"}`}>
          {extractResource(resourceData, "passwordsDoNotMatch")}
        </p>

        <Button
          color="primary"
          type="submit"
          variant="contained"
          fullWidth
          disabled={
            !hasANumber ||
            !hasTwelveChars ||
            !hasUpperCase ||
            !hasLowerCase ||
            !hasSpecialChar ||
            !matchedPassword ||
            confirmPassword.length < MINIMUM_PASSWORD_LENGTH ||
            confirmPassword.length > MAXMIUM_PASSWORD_LENGTH
          }
          onClick={handleSubmit}
          isLoading={isLoading}
        >
          {extractResource(resourceData, "confirmAndSignIn")}
        </Button>
      </div>
    </div>
  );
}

export default ForgotPasswordResetPage;
